"use client";

import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  insertDiscount,
  updateDiscount,
  findAllDiscount,
  findByIDDiscount,
} from "./discount.service";
import { discountValidationSchema } from "./discount.validation";
import { clearDiscountDetails } from "./discount.slice";
import { closed } from "@/redux/slices/drawerSlice";
import { STATUS_OPTIONS } from "@/lib/constants";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { CustomButton } from "@/components/common/CustomButton";
import { Save, X } from "lucide-react";
import { FormLoader } from "@/components/common/FormLoader";

const DiscountForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { DiscountDetail, formLoading, loading } = useAppSelector(
    (state) => state.discount,
  );
  const { data: DiscountID } = useAppSelector((state) => state.drawer);

  useEffect(() => {
    if (DiscountID) {
      dispatch(findByIDDiscount(DiscountID));
    }
  }, [DiscountID, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearDiscountDetails());
    };
  }, [dispatch]);

  const handleSubmit = async (values: any) => {
    const payload: any = {
      FromValue: values?.FromValue ? +values?.FromValue : 0,
      ToValue: values?.ToValue ? +values?.ToValue : 0,
      DiscountPercent: values?.DiscountPercent ? +values?.DiscountPercent : 0,
      IsActive: values?.IsActive || false,
    };

    if (DiscountID) {
      payload.ID = DiscountID || null;
      await dispatch(updateDiscount(payload));
    } else {
      await dispatch(insertDiscount(payload));
    }

    dispatch(findAllDiscount());
    dispatch(closed());
  };

  return (
    <div className="relative">
      <FormLoader loading={formLoading || loading} />
      <Formik
        initialValues={{
          FromValue: DiscountDetail?.FromValue ?? "",
          ToValue: DiscountDetail?.ToValue ?? "",
          DiscountPercent: DiscountDetail?.DiscountPercent ?? "",
          IsActive: DiscountDetail?.IsActive ?? true,
        }}
        validationSchema={discountValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="flex flex-col gap-6">
            <div className="space-y-4">
              <InputField
                label="From Value"
                name="FromValue"
                type="number"
                placeholder="Enter From Value"
                required
              />

              <InputField
                label="To Value"
                name="ToValue"
                type="number"
                placeholder="Enter To Value"
                required
              />

              <InputField
                label="Discount Percent (%)"
                name="DiscountPercent"
                type="number"
                max={100}
                placeholder="Enter discount percentage"
                required
              />

              <SelectField
                label="Status"
                name="IsActive"
                options={STATUS_OPTIONS}
                required
              />
            </div>

            <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-100">
              <CustomButton
                type="submit"
                variant="primary"
                loading={formLoading}
                icon={<Save size={14} />}
                fullWidth
              >
                {DiscountID ? "Update" : "Insert"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="outline"
                icon={<X size={14} />}
                onClick={() => dispatch(closed())}
                disabled={formLoading}
                fullWidth
              >
                Cancel
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default DiscountForm;
